import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { AlertTriangle, CheckCircle2, FileText, Loader2, Lock, Trash2, Upload } from "lucide-react";
import { API, testApi, getTestToken, setTestToken } from "@/lib/testApi";

/**
 * Live workspace bootstrap flow.
 *
 * Steps:
 *   1. Verify authenticated test user; if missing, redirect to /access.
 *   2. Redeem QA code → provision empty private_live workspace.
 *   3. Consent gate: sign name + accept authorization policy.
 *   4. Show workspace state + allow creating opportunities + uploads + delete.
 */
export default function LiveStart() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const pathParam = (params.get("path") || "").toLowerCase();
  const pathIntent = pathParam === "company" ? "company" : "investor";

  const [me, setMe] = useState(null);
  const [loadingMe, setLoadingMe] = useState(true);

  const [workspace, setWorkspace] = useState(null);
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);

  const [consentAccepted, setConsentAccepted] = useState(false);
  const [signature, setSignature] = useState("");
  const [savingConsent, setSavingConsent] = useState(false);

  const [oppName, setOppName] = useState("");
  const [oppThesis, setOppThesis] = useState("");
  const [creatingOpp, setCreatingOpp] = useState(false);
  const [opps, setOpps] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ---------- Auth check ----------
  useEffect(() => {
    document.title = "Private Live Workspace | ImpactVest";
    const token = getTestToken();
    if (!token) {
      navigate(`/access?next=/live/start?path=${pathIntent}`, { replace: true });
      return;
    }
    testApi.get("/test/me")
      .then((r) => setMe(r.data.user))
      .catch(() => {
        setTestToken(null);
        navigate("/access", { replace: true });
      })
      .finally(() => setLoadingMe(false));
  }, [navigate, pathIntent]);

  // ---------- Load existing live workspaces for this user ----------
  const loadWorkspace = useCallback(async () => {
    try {
      const r = await testApi.get("/live/workspaces");
      const list = (r.data.workspaces || []).filter((w) => w.pathway === pathIntent);
      if (list.length > 0) {
        const ws = list[0];
        setWorkspace(ws);
        // Load its opportunities.
        try {
          const o = await testApi.get("/test/opportunities");
          const wsOpps = (o.data.opportunities || []).filter(
            (opp) => opp.workspace_id === ws.id && opp.data_environment === "private_live",
          );
          setOpps(wsOpps);
        } catch { /* ignore */ }
      }
    } catch (err) {
      // no workspaces yet — that's fine.
    }
  }, [pathIntent]);

  useEffect(() => { if (me) loadWorkspace(); }, [me, loadWorkspace]);

  // ---------- Actions ----------
  async function handleRedeem() {
    if (!code.trim()) { toast.error("Please enter your access code."); return; }
    setRedeeming(true);
    try {
      const r = await testApi.post("/live/redeem-code", { code: code.trim() });
      setWorkspace(r.data.workspace);
      toast.success("Private workspace ready.");
      setCode("");
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = (d && typeof d === "object" && d.message) ? d.message
                : (typeof d === "string" ? d : "Unable to redeem this code.");
      toast.error(msg);
    } finally {
      setRedeeming(false);
    }
  }

  async function handleConsent() {
    if (!workspace) return;
    if (!consentAccepted) { toast.error("Please tick the authorization box."); return; }
    if (signature.trim().length < 2) { toast.error("Please type your full name to sign."); return; }
    setSavingConsent(true);
    try {
      const r = await testApi.post(`/live/workspaces/${workspace.id}/consent`, {
        accepted: true,
        signature_full_name: signature.trim(),
      });
      setWorkspace(r.data.workspace);
      toast.success("Authorization recorded. Uploads unlocked.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = (d && d.message) || "Unable to record consent.";
      toast.error(msg);
    } finally {
      setSavingConsent(false);
    }
  }

  async function handleCreateOpp() {
    if (!workspace) return;
    if (oppName.trim().length < 2) { toast.error("Please give the opportunity a name."); return; }
    setCreatingOpp(true);
    try {
      const r = await testApi.post(`/live/workspaces/${workspace.id}/opportunities`, {
        name: oppName.trim(),
        thesis: oppThesis.trim(),
      });
      setOpps((prev) => [r.data.opportunity, ...prev]);
      setOppName(""); setOppThesis("");
      toast.success("Opportunity created.");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Unable to create opportunity.");
    } finally {
      setCreatingOpp(false);
    }
  }

  async function handleUpload(opp, file) {
    if (!file) return;
    setUploading(opp.id);
    const form = new FormData();
    form.append("file", file);
    try {
      await testApi.post(`/test/opportunities/${opp.id}/documents`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded ${file.name}`);
      // Refresh list.
      const o = await testApi.get("/test/opportunities");
      const wsOpps = (o.data.opportunities || []).filter(
        (op) => op.workspace_id === workspace.id && op.data_environment === "private_live",
      );
      setOpps(wsOpps);
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = (d && d.message) || "Upload failed.";
      toast.error(msg);
    } finally {
      setUploading(null);
    }
  }

  async function handleDeleteWorkspace() {
    if (!workspace) return;
    if (!window.confirm(
      "Permanently delete this workspace? All uploaded documents, extracts, analyses, claims, revisions and released packs will be removed. This cannot be undone."
    )) return;
    setDeleting(true);
    try {
      const r = await testApi.delete(`/live/workspaces/${workspace.id}`);
      toast.success(`Workspace deleted. Removed ${r.data.audit.counts_removed.gridfs_files} file(s).`);
      setWorkspace(null);
      setOpps([]);
      setConsentAccepted(false);
      setSignature("");
    } catch {
      toast.error("Unable to delete workspace.");
    } finally {
      setDeleting(false);
    }
  }

  const consentComplete = !!workspace?.authorization_confirmed_at;

  if (loadingMe) {
    return <FullScreenMsg>Loading your workspace…</FullScreenMsg>;
  }

  return (
    <div className="min-h-screen bg-iv-bg text-iv-fg" data-testid="live-start-page">
      <header className="border-b border-iv-line bg-iv-surface">
        <div className="iv-container flex items-center justify-between h-[68px]">
          <div className="flex items-center gap-3">
            <span className="iv-caption" style={{ color: "var(--iv-fg-2)" }}>
              {pathIntent === "company" ? "Private company workspace · Confidential" : "Private investor workspace · Confidential"}
            </span>
          </div>
          <button
            data-testid="live-signout-btn"
            onClick={() => { setTestToken(null); navigate("/"); }}
            className="iv-body-sm px-3 py-2 min-h-[44px] hover:text-iv-fg"
            style={{ color: "var(--iv-fg-2)" }}
          >Sign out</button>
        </div>
      </header>

      <main className="iv-container py-14 md:py-20 max-w-[820px] mx-auto">
        <h1 className="iv-h1 mb-6">
          {pathIntent === "company" ? "Private company workspace" : "Private investor workspace"}
        </h1>
        <p className="iv-body-lg mb-12">
          Scoped to your account. Documents you upload are analysed by ImpactVest and retained
          for the disclosed period below. You can remove this workspace and every document, extract,
          claim and released pack it contains at any time from this page.
        </p>

        {/* Step 1 · Redeem */}
        {!workspace && (
          <Section title="Enter your access code" testid="step-redeem">
            <p className="iv-body mb-6">
              You should have received a single-use access code from ImpactVest. Enter it below to
              open your private workspace. This screen is temporary — once Stripe
              activation is live, the workspace is created automatically after payment.
            </p>
            <div className="flex flex-col md:flex-row gap-3 max-w-[560px]">
              <input
                data-testid="live-code-input"
                type="text" autoComplete="off" spellCheck={false}
                value={code} onChange={(e) => setCode(e.target.value)}
                placeholder="Paste access code"
                className="flex-1 border border-iv-line-strong bg-iv-surface px-4 py-3 text-[16px] min-h-[44px]"
                style={{ fontFamily: "'Geist','Inter',system-ui,sans-serif" }}
              />
              <button
                data-testid="live-redeem-btn"
                onClick={handleRedeem}
                disabled={redeeming || !code.trim()}
                className="iv-btn iv-btn--primary min-w-[180px]"
              >{redeeming ? <><Loader2 size={16} className="animate-spin" /> Redeeming…</> : "Redeem code →"}</button>
            </div>
          </Section>
        )}

        {/* Step 2 · Consent + Retention */}
        {workspace && !consentComplete && (
          <Section title="Confirm authorization" testid="step-consent">
            <div className="border border-iv-line-strong bg-iv-surface p-7 md:p-8 mb-4">
              <div className="flex items-start gap-3 mb-6">
                <Lock size={20} className="text-iv-gold shrink-0 mt-1" />
                <div>
                  <div className="iv-h4 mb-2" style={{ color: "var(--iv-fg)" }}>Retention</div>
                  <p className="iv-body-sm" style={{ maxWidth: "68ch" }}>
                    Retention of this workspace is <b>customer-controlled</b>. Documents, extracts,
                    claims, drafts and released packs remain until you delete the workspace from this
                    page. Deletion removes all workspace contents and writes a content-free audit record.
                    Consent records are kept as legal evidence of authorisation.
                  </p>
                  <a
                    href="/legal/retention" target="_blank" rel="noopener noreferrer"
                    data-testid="live-retention-notice-link"
                    className="iv-body-sm mt-2 inline-block"
                    style={{ color: "var(--iv-green)", textDecoration: "underline", fontWeight: 500 }}
                  >
                    Read the full retention notice →
                  </a>
                </div>
              </div>
              <label className="flex items-start gap-3 cursor-pointer mb-6 max-w-[68ch]">
                <input
                  data-testid="live-consent-checkbox"
                  type="checkbox" checked={consentAccepted}
                  onChange={(e) => setConsentAccepted(e.target.checked)}
                  className="mt-1.5 w-[18px] h-[18px]"
                />
                <span className="iv-body-sm">
                  I am authorised to upload the materials that will be added to this workspace and to have them
                  analysed by ImpactVest. I have read the <a href="/legal/retention" target="_blank" rel="noopener noreferrer" style={{ color: "var(--iv-green)", textDecoration: "underline" }}>retention notice</a>.
                  This workspace is for real diligence documents; the fictional-demonstration watermark used in
                  the Test Workspace is not applied here.
                </span>
              </label>
              <div className="mb-5">
                <label className="block mb-2 iv-caption" style={{ color: "var(--iv-fg-2)", fontWeight: 500 }}>Signed name</label>
                <input
                  data-testid="live-consent-signature"
                  type="text" value={signature} onChange={(e) => setSignature(e.target.value)}
                  placeholder="Type your full name"
                  className="w-full max-w-[440px] border border-iv-line-strong bg-white px-4 py-3 text-[16px] min-h-[44px]"
                  style={{ fontFamily: "'Geist','Inter',system-ui,sans-serif" }}
                />
              </div>
              <button
                data-testid="live-consent-submit"
                onClick={handleConsent}
                disabled={savingConsent || !consentAccepted || signature.trim().length < 2}
                className="iv-btn iv-btn--primary"
              >{savingConsent ? "Recording…" : "Confirm authorization →"}</button>
            </div>
          </Section>
        )}

        {/* Step 3 · Workspace body */}
        {workspace && consentComplete && (
          <Section title="Your workspace" testid="step-workspace">
            <div className="border border-iv-line-strong bg-iv-surface p-6 md:p-7 mb-8">
              <div className="flex items-center gap-2 iv-body-sm mb-2">
                <CheckCircle2 size={18} className="text-iv-green" />
                <span>Authorization recorded by <b>{workspace.authorization_confirmed_by}</b> on {new Date(workspace.authorization_confirmed_at).toLocaleString()}.</span>
              </div>
              <div className="iv-caption">
                Consent policy · {workspace.authorization_policy_version} · Retention notice v{workspace.retention_notice_version || "1.2026-02"} · <a href="/legal/retention" target="_blank" rel="noopener noreferrer" style={{ color: "var(--iv-green)", textDecoration: "underline" }}>Read the notice</a>
              </div>
            </div>

            {/* Create opportunity */}
            <div className="border border-iv-line-strong bg-white p-6 md:p-7 mb-8">
              <h3 className="iv-h4 mb-4" style={{ color: "var(--iv-fg)" }}>Add opportunity</h3>
              <div className="flex flex-col gap-4">
                <input
                  data-testid="live-opp-name"
                  type="text" value={oppName} onChange={(e) => setOppName(e.target.value)}
                  placeholder="Opportunity name (e.g. Series A pack · Terravise)"
                  className="border border-iv-line-strong bg-iv-bg px-4 py-3 text-[16px] min-h-[44px]"
                  style={{ fontFamily: "'Geist','Inter',system-ui,sans-serif" }}
                />
                <textarea
                  data-testid="live-opp-thesis"
                  value={oppThesis} onChange={(e) => setOppThesis(e.target.value)}
                  placeholder="Short thesis or context (optional)"
                  className="border border-iv-line-strong bg-iv-bg px-4 py-3 text-[16px] min-h-[96px]"
                  style={{ fontFamily: "'Geist','Inter',system-ui,sans-serif", lineHeight: 1.5 }}
                />
                <button
                  data-testid="live-opp-create"
                  onClick={handleCreateOpp}
                  disabled={creatingOpp || oppName.trim().length < 2}
                  className="iv-btn iv-btn--primary self-start"
                >{creatingOpp ? "Creating…" : "Create opportunity"}</button>
              </div>
            </div>

            {/* Opportunities list */}
            {opps.length === 0 ? (
              <div className="border border-dashed border-iv-line-strong p-10 text-center">
                <FileText size={22} className="mx-auto mb-3" style={{ color: "var(--iv-fg-3)" }} />
                <div className="iv-body-sm" style={{ color: "var(--iv-fg-2)" }}>
                  No opportunities yet. Create one above to start uploading materials.
                </div>
              </div>
            ) : (
              <div className="space-y-4 mb-10">
                {opps.map((opp) => (
                  <OpportunityCard
                    key={opp.id}
                    opp={opp}
                    onUpload={(file) => handleUpload(opp, file)}
                    uploading={uploading === opp.id}
                  />
                ))}
              </div>
            )}

            {/* Delete workspace */}
            <div className="mt-14 pt-8 border-t border-iv-line-strong">
              <button
                data-testid="live-workspace-delete"
                onClick={handleDeleteWorkspace}
                disabled={deleting}
                className="inline-flex items-center gap-2 iv-body-sm hover:underline min-h-[44px]"
                style={{ color: "var(--iv-danger)", fontWeight: 500 }}
              >
                <Trash2 size={18} />
                {deleting ? "Deleting…" : "Delete this workspace and all its contents"}
              </button>
              <div className="iv-caption mt-2 max-w-[68ch]">
                Removes every uploaded document, extract, claim, revision, released pack version and stored blob, and writes a content-free audit record.
              </div>
            </div>
          </Section>
        )}
      </main>
    </div>
  );
}

function OpportunityCard({ opp, onUpload, uploading }) {
  const docCount = (opp.documents || []).length;
  return (
    <div className="border border-iv-line-strong bg-white p-6" data-testid={`live-opp-${opp.id}`}>
      <div className="flex justify-between items-start gap-6 mb-3">
        <div>
          <h4 className="iv-h4" style={{ fontFamily: "'Cormorant Garamond','Georgia',serif", fontSize: "26px", lineHeight: 1.15, letterSpacing: "-0.005em" }}>{opp.name}</h4>
          {opp.thesis && <p className="iv-body-sm mt-2" style={{ maxWidth: "60ch" }}>{opp.thesis}</p>}
        </div>
        <span className="iv-caption shrink-0" style={{ textTransform: "capitalize" }}>
          Private live · {opp.pathway}
        </span>
      </div>
      <div className="flex items-center gap-4 mt-5">
        <label className="iv-btn cursor-pointer">
          <Upload size={16} />
          {uploading ? "Uploading…" : "Upload document"}
          <input
            data-testid={`live-upload-${opp.id}`}
            type="file" hidden
            accept=".pdf,.docx,.xlsx,.csv,application/pdf"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUpload(f);
              e.target.value = "";
            }}
            disabled={uploading}
          />
        </label>
        <span className="iv-body-sm" style={{ color: "var(--iv-fg-3)" }}>
          {docCount === 0 ? "No documents uploaded yet." : `${docCount} document${docCount > 1 ? "s" : ""} uploaded`}
        </span>
      </div>
    </div>
  );
}

function Section({ title, testid, children }) {
  return (
    <section className="mb-14" data-testid={testid}>
      <h2 className="iv-h3 mb-5" style={{ color: "var(--iv-fg)" }}>{title}</h2>
      {children}
    </section>
  );
}

function FullScreenMsg({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-iv-bg iv-body" style={{ color: "var(--iv-fg-3)" }}>
      {children}
    </div>
  );
}
